import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Header } from '../../Components/Header/Header';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import { VendingMeta } from '../../Types/vending';

interface CustomerRow {
  cid: number;
  customer_name: string;
  machine_count: number;
  online_count: number;
  offline_count: number;
  mapped_count: number;
  open_alerts: number;
}

interface CustomerListResponse {
  data: CustomerRow[];
  meta: VendingMeta;
}

const SEARCH_DEBOUNCE_MS = 300;

const headCellSx = {
  py: 1.25,
  px: 2,
  fontWeight: 700,
  fontSize: '0.6875rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: COLORS.lightGray,
  backgroundColor: '#f8f9fa',
  borderBottom: `1px solid ${COLORS.lightGray}35`,
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  py: 1.75,
  px: 2,
  borderBottom: `1px solid ${COLORS.lightGray}30`,
  verticalAlign: 'middle' as const,
};

export const VendingCustomersPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendingApiClient('/customers');
      const json: CustomerListResponse = await response.json();
      setRows(json.data);
      setAsOf(json.meta?.as_of ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load customers');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!debouncedSearch) return rows;
    return rows.filter((r) => (
      r.customer_name.toLowerCase().includes(debouncedSearch)
      || String(r.cid).includes(debouncedSearch)
    ));
  }, [rows, debouncedSearch]);

  const totals = useMemo(() => ({
    machines: rows.reduce((n, r) => n + r.machine_count, 0),
    online: rows.reduce((n, r) => n + r.online_count, 0),
    alerts: rows.reduce((n, r) => n + r.open_alerts, 0),
  }), [rows]);

  const subtitle = useMemo(() => {
    const parts = [
      `${rows.length} customer${rows.length === 1 ? '' : 's'}`,
      `${totals.machines} machines`,
      `${totals.online} online`,
    ];
    if (totals.alerts) parts.push(`${totals.alerts} open alerts`);
    if (asOf) parts.push(`As of ${formatUTC(asOf)} UTC`);
    return parts.join(' · ');
  }, [rows.length, totals, asOf]);

  return (
    <PageContainer>
      <Header title="Customers" subtitle={subtitle} count={filtered.length}>
        <FreshnessBadge asOf={asOf} />
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: 2,
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Customer name or CID…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 1 280px' } }}
          />
          {searchInput && (
            <Button variant="text" onClick={() => setSearchInput('')}>Clear</Button>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: { xs: 2, md: 3 } }}>
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
            >
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Box
            sx={{
              mx: { xs: 2, md: 3 },
              textAlign: 'center',
              py: 8,
              px: 2,
              borderRadius: 2,
              border: `1px dashed ${COLORS.lightGray}`,
              backgroundColor: '#fafbfc',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
              No customers
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
              {rows.length === 0
                ? 'Map machines to an AIMS customer to see them here.'
                : 'No customers match the current search.'}
            </Typography>
          </Box>
        )}

        {!loading && !error && filtered.length > 0 && (
          <Box sx={{ flex: 1, minHeight: 0, px: { xs: 2, md: 3 }, pb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${COLORS.lightGray}40`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Customer</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Machines</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Online</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Offline</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Mapped</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Open alerts</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((row) => {
                      const onlinePct = row.machine_count
                        ? Math.round((row.online_count / row.machine_count) * 100)
                        : 0;
                      return (
                        <TableRow
                          key={row.cid}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#fafbfc' },
                          }}
                          onClick={() => navigate(`/vending/customers/${row.cid}`)}
                        >
                          <TableCell sx={bodyCellSx}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.darkGray }}>
                              {row.customer_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                              CID {row.cid} · {onlinePct}% online
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {row.machine_count}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: row.online_count > 0 ? COLORS.success : COLORS.darkGray }}
                            >
                              {row.online_count}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                            <Typography
                              variant="body2"
                              fontWeight={row.offline_count > 0 ? 700 : 400}
                              sx={{ color: row.offline_count > 0 ? COLORS.red : COLORS.darkGray }}
                            >
                              {row.offline_count}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                            <Typography variant="body2">{row.mapped_count}</Typography>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                            {row.open_alerts > 0 ? (
                              <Chip
                                size="small"
                                label={row.open_alerts}
                                sx={{
                                  height: 22,
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  bgcolor: `${COLORS.orange}18`,
                                  color: COLORS.orange,
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: COLORS.lightGray }}>0</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};
